import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiChevronDown, FiWifiOff } from 'react-icons/fi';
import PricingActivityCard from './PricingActivityCard';
import useCurrentUser from '../hooks/useCurrentUser';
import { listPricingActivities, subscribePricingRequests } from '../api/pricingActivityApi';
import { getSelectedCompanyId, subscribeSelectedCompany } from '../utils/selectedCompany';
import { notifyProductEvent } from '../utils/productEventsHub';

const terminal = (activity) => ['completed', 'partial', 'failed', 'interrupted'].includes(activity.status);
const storageKey = (companyId, userId) => `pricing-activity-dismissed:${companyId}:${userId}`;
const readDismissed = (key) => {
  try { return new Set(JSON.parse(sessionStorage.getItem(key) || '[]')); } catch { return new Set(); }
};

const PricingActivityIndicator = () => {
  const { user, status } = useCurrentUser();
  const location = useLocation();
  const listId = useId();
  const [companyId, setCompanyId] = useState(getSelectedCompanyId);
  const [activities, setActivities] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const stateRef = useRef(null);
  const relevant = /\/branches\/\d+\/(?:price-list|stock|purchases\/(?:new|\d+\/edit)|catalog)|\/settings\/prices/.test(location.pathname);
  const relevantRef = useRef(relevant);
  relevantRef.current = relevant;
  useEffect(() => subscribeSelectedCompany(setCompanyId), []);

  useEffect(() => {
    const key = storageKey(companyId, user?.id);
    const observedKey = `${key}:observed`;
    const state = { active: true, startedAt: Date.now(), values: new Map(), observed: readDismissed(observedKey), dismissed: readDismissed(key), reconciling: new Set(), done: new Set(), timers: new Set(), key };
    stateRef.current = state;
    setActivities([]);
    if (!companyId || !user?.id || status !== 'ready') return () => { state.active = false; };
    const render = () => {
      if (state.active) setActivities([...state.values.values()].filter((activity) => !state.dismissed.has(activity.id)));
    };
    state.dismiss = (id) => {
      state.dismissed.add(id);
      state.observed.delete(id);
      try { sessionStorage.setItem(key, JSON.stringify([...state.dismissed].slice(-200))); } catch { /* Optional storage. */ }
      try { sessionStorage.setItem(observedKey, JSON.stringify([...state.observed])); } catch { /* Optional storage. */ }
      render();
    };
    const reconcile = async (activity) => {
      if (state.done.has(activity.id) || state.reconciling.has(activity.id)) return;
      state.reconciling.add(activity.id);
      state.values.set(activity.id, { ...activity, stage: 'refreshing' });
      render();
      try {
        await Promise.all((activity.changes || []).map((change) => notifyProductEvent({
          ...change, type: 'products.invalidated', action: 'catalog-structure-updated',
          companyId: activity.companyId, activityId: activity.id,
        })));
        if (!state.active) return;
        state.done.add(activity.id);
        state.values.set(activity.id, { ...activity, stage: 'completed' });
        const timer = setTimeout(() => { state.dismiss(activity.id); state.timers.delete(timer); }, 5000);
        state.timers.add(timer);
      } catch {
        if (state.active) state.values.set(activity.id, { ...activity, stage: 'refresh-error', error: 'Los cambios se guardaron, pero no se pudo actualizar la vista. Reintentando…' });
      } finally {
        state.reconciling.delete(activity.id);
        render();
      }
    };
    const accept = (activity) => {
      if (!state.active || String(activity.companyId) !== String(companyId) || (activity.userId && String(activity.userId) !== String(user.id)) || state.dismissed.has(activity.id)) return;
      const previous = state.values.get(activity.id);
      // Initial catalog loading already reconciles completed history. Only show
      // new completions or work this tab observed before navigation/reconnection.
      if (!previous && !state.observed.has(activity.id) && activity.status === 'completed'
        && new Date(activity.completedAt || activity.updatedAt || activity.createdAt).getTime() <= state.startedAt) return;
      if (previous && terminal(previous) && !terminal(activity)) return;
      if (state.done.has(activity.id) || state.reconciling.has(activity.id)) return;
      state.observed.add(activity.id);
      try { sessionStorage.setItem(observedKey, JSON.stringify([...state.observed].slice(-200))); } catch { /* Optional storage. */ }
      state.values.set(activity.id, activity);
      if (terminal(activity)) void reconcile(activity);
      render();
    };
    let timer;
    let polling = false;
    let discovered = false;
    const poll = async () => {
      if (!state.active || polling) return;
      polling = true;
      const hasActive = [...state.values.values()].some((activity) => !terminal(activity) || !state.done.has(activity.id));
      if (!discovered || relevantRef.current || hasActive) {
        try {
          const rows = await listPricingActivities();
          if (!state.active) return;
          setConnectionError(false);
          discovered = true;
          (rows || []).forEach(accept);
          state.values.forEach((activity) => {
            if (activity.status === 'recovering' && Date.now() - new Date(activity.createdAt).getTime() > 90000 && !(rows || []).some((row) => row.id === activity.id)) {
              accept({ ...activity, status: 'interrupted', error: 'No se pudo confirmar la operación. Revisa sus resultados antes de reintentar.' });
            }
          });
        } catch { if (state.active) setConnectionError(true); }
      }
      polling = false;
      if (state.active) timer = setTimeout(poll, [...state.values.values()].some((activity) => !terminal(activity) || !state.done.has(activity.id)) ? 1000 : 5000);
    };
    const unsubscribe = subscribePricingRequests(({ activity }) => { accept(activity); clearTimeout(timer); void poll(); });
    void poll();
    const wake = () => { clearTimeout(timer); void poll(); };
    window.addEventListener('online', wake);
    window.addEventListener('focus', wake);
    return () => {
      state.active = false;
      clearTimeout(timer);
      state.timers.forEach(clearTimeout);
      unsubscribe();
      window.removeEventListener('online', wake);
      window.removeEventListener('focus', wake);
    };
  }, [companyId, user?.id, status]);

  if (!activities.length) return null;
  const shown = expanded ? activities : activities.slice(0, 1);
  return <aside aria-label="Progreso de precios" className="pricing-activity-panel">
    <div className="pricing-activity-panel__header">
      <strong className="pricing-activity-panel__title">Actualización de precios
        {activities.length > 1 && <span className="pricing-activity-panel__badge">{activities.length}</span>}
      </strong>
      <button type="button" className="pricing-activity-panel__toggle" aria-expanded={expanded} aria-controls={listId}
        onClick={() => setExpanded((value) => !value)}>{expanded ? 'Contraer' : 'Ver detalles'}<FiChevronDown aria-hidden="true" /></button>
    </div>
    <div id={listId} className="pricing-activity-panel__list">
      {shown.map((activity) => <PricingActivityCard key={activity.id} activity={activity}
        expanded={expanded} connectionError={connectionError} onDismiss={() => stateRef.current?.dismiss?.(activity.id)} />)}
    </div>
    {connectionError && <p role="status" className="pricing-activity-panel__connection"><FiWifiOff aria-hidden="true" />Sin conexión al progreso. Reintentando…</p>}
  </aside>;
};

export default PricingActivityIndicator;
